import { ref } from 'vue'
import axios from '@/services/axios'

type FlagsmithOnChange = (oldFlags: Record<string, any>, params: any) => void

type FlagsmithFlag = {
  featureName?: string
  enabled?: boolean
  value?: unknown
  [key: string]: any
}

type FlagsMap = Record<string, FlagsmithFlag>

const isInitialized = ref(false)
const isInitializing = ref(false)
const flags = ref<FlagsMap>({})
const changeListeners: FlagsmithOnChange[] = []

const normalizeFlags = (list: FlagsmithFlag[] | undefined | null): FlagsMap => {
  if (!Array.isArray(list)) return {}
  const normalized: FlagsMap = {}

  for (const flag of list) {
    const name = flag?.featureName
    if (!name) continue
    normalized[name] = flag
  }

  return normalized
}

const fetchFlags = async (): Promise<FlagsMap | null> => {
  try {
    const response = await axios.get('/flagsmith', { showSnackbar: false })
    if (!response.data?.success) {
      console.warn('Flagsmith: request did not succeed', response.data)
      return null
    }

    const normalized = normalizeFlags(response.data.flags)
    if (!Object.keys(normalized).length) {
      console.warn('Flagsmith: no valid flags returned', response.data)
    }
    return normalized
  } catch (e) {
    console.error('Flagsmith request failed:', e)
    return null
  }
}

export function useFeatureFlag() {
  const init = async (options?: { environmentID?: string; onChange?: FlagsmithOnChange }) => {
    if (options?.onChange) {
      changeListeners.push(options.onChange)
    }

    if (isInitialized.value || isInitializing.value) {
      return
    }

    isInitializing.value = true

    const newFlags = await fetchFlags()
    const hadError = !newFlags

    if (newFlags) {
      flags.value = newFlags
      isInitialized.value = true

      for (const cb of changeListeners) {
        try {
          cb({}, {})
        } catch {
        }
      }
    } else if (hadError) {
      isInitialized.value = true
    }

    isInitializing.value = false
  }

  const getFlag = (key: string): FlagsmithFlag | undefined => {
    return flags.value[key]
  }

  const hasFeature = (key: string): boolean => {
    const flag = getFlag(key)
    return flag?.enabled === true
  }

  const getValue = (key: string): unknown => {
    const flag = getFlag(key)
    return flag && 'value' in flag ? flag.value : undefined
  }

  /** Matches crisp_user / crisp_enable_all style checks on remote config value. */
  const isTruthyRemoteValue = (v: unknown): boolean =>
    v === true || v === 'true' || v === 1 || v === '1'

  /**
   * Feature on only when the Flagsmith toggle is on (`enabled`), then optionally gated by value.
   * Ignores stale remote config when the toggle is off (e.g. value still `true` after disabling).
   */
  const isFeatureEnabledLikeCrisp = (key: string): boolean => {
    const flag = getFlag(key)
    if (!flag || flag.enabled !== true) return false
    if (!('value' in flag)) return true
    const v = flag.value
    if (v === undefined || v === null) return true
    if (typeof v === 'string' && v.trim() === '') return true
    return isTruthyRemoteValue(v)
  }

  const onChange = (cb: FlagsmithOnChange) => {
    changeListeners.push(cb)
  }

  const refresh = async () => {
    const newFlags = await fetchFlags()
    if (!newFlags) return

    const oldFlags = { ...flags.value }
    flags.value = newFlags

    for (const cb of changeListeners) {
      try {
        cb(oldFlags, {})
      } catch {
      }
    }
  }

  return {
    init,
    isInitialized,
    getFlag,
    hasFeature,
    getValue,
    isTruthyRemoteValue,
    isFeatureEnabledLikeCrisp,
    onChange,
    refresh,
    flags,
  }
}

